import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { useUserProfile } from "../../contexts/UserProfileContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";

type FaqRow = {
  id: string;
  slug: string | null;
  title: string;
  body_md: string;
  tags: string[] | null;
  status: "draft" | "published";
  created_at: string;
  updated_at: string;
};

export default function AdminFAQEdit() {
  const { profile } = useUserProfile();
  const { id } = useParams<{ id: string }>(); // "new" | uuid
  const navigate = useNavigate();

  const isNew = id === "new";

  const [row, setRow] = useState<FaqRow | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [tagsStr, setTagsStr] = useState("");
  const [status, setStatus] = useState<"draft" | "published">("draft");
  const [slug, setSlug] = useState<string | null>(null);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tagsArray = useMemo(
    () =>
      tagsStr
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    [tagsStr]
  );

  useEffect(() => {
    if (!profile || profile.role !== "admin") return;
    if (isNew) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      const { data, error } = await supabase
        .from("faq_entries")
        .select("*")
        .eq("id", id!)
        .maybeSingle();

      if (error || !data) {
        setError("Eintrag konnte nicht geladen werden.");
        setLoading(false);
        return;
      }

      setRow(data as FaqRow);
      setTitle(data.title);
      setBody(data.body_md);
      setTagsStr((data.tags ?? []).join(", "));
      setStatus(data.status as "draft" | "published");
      setSlug(data.slug);
      setLoading(false);
    };
    load();
  }, [id, isNew, profile?.role]);

  if (!profile || profile.role !== "admin") {
    return <div className="text-center text-red-400 mt-10">Kein Zugriff – nur für Admins 🛑</div>;
  }

  if (!id) return null;

  const save = async () => {
    setSaving(true);
    setError(null);

    if (isNew) {
      const { data, error } = await supabase
        .from("faq_entries")
        .insert([
          {
            title: title.trim(),
            body_md: body,
            tags: tagsArray,
            status,
            // slug wird automatisch via Trigger gesetzt
          },
        ])
        .select()
        .single();

      if (error) {
        console.error("insert failed:", error.message);
        setError("Speichern fehlgeschlagen.");
      } else {
        // nach Erstellen direkt in Edit wechseln
        navigate(`/admin/faq/${data.id}`);
      }
    } else {
      const { error } = await supabase
        .from("faq_entries")
        .update({
          title: title.trim(),
          body_md: body,
          tags: tagsArray,
          status,
          // slug: leer lassen → bleibt oder wird vom Trigger bei Titeländerung neu berechnet
        })
        .eq("id", id!);

      if (error) {
        console.error("update failed:", error.message);
        setError("Speichern fehlgeschlagen.");
      } else {
        // optional reload
        const { data } = await supabase.from("faq_entries").select("*").eq("id", id!).maybeSingle();
        if (data) {
          setRow(data as FaqRow);
          setSlug(data.slug);
        }
      }
    }
    setSaving(false);
  };

  const remove = async () => {
    if (isNew) return;
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    const { error } = await supabase.from("faq_entries").delete().eq("id", id!);
    if (error) {
      console.error("delete failed:", error.message);
      setError("Löschen fehlgeschlagen.");
    } else {
      navigate("/admin/faq");
    }
  };

  return (
    <div className="max-w-5xl mx-auto mt-10 p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-sky-400">
            {isNew ? "Neuen FAQ-Eintrag erstellen" : "FAQ-Eintrag bearbeiten"}
          </h1>
          {!isNew && slug && (
            <p className="text-xs text-slate-500">
              Öffentliche URL:{" "}
              <a className="text-sky-400 hover:underline" href={`/faq/${slug}`} target="_blank" rel="noreferrer">
                /faq/{slug}
              </a>
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {!isNew && (
            <button onClick={remove} className="px-3 py-2 rounded-lg border border-[#2a3347] text-red-400 hover:bg-[#1e2639] transition-colors">
              Löschen
            </button>
          )}
          <button
            onClick={save}
            disabled={saving || !title.trim() || !body.trim()}
            className={`px-4 py-2 rounded-lg transition-colors ${saving || !title.trim() || !body.trim()
              ? "bg-[#2a3347] text-slate-500"
              : "bg-sky-500 text-white hover:bg-sky-600"
            }`}
          >
            {saving ? "Speichere…" : "Speichern"}
          </button>
        </div>
      </div>

      {error && <p className="text-red-400 mb-4">{error}</p>}

      {loading ? (
        <p className="text-slate-400">⏳ Lade…</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-400">Titel</label>
            <input
              className="w-full border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded-lg px-3 py-2 outline-none focus:border-sky-400 placeholder:text-slate-600"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Kurzer, aussagekräftiger Titel"
            />

            <label className="block text-sm font-medium text-slate-400">Status</label>
            <select
              className="w-full border border-[#2a3347] bg-[#0d1117] text-slate-200 rounded-lg px-3 py-2 outline-none focus:border-sky-400"
              value={status}
              onChange={(e) => setStatus(e.target.value as "draft" | "published")}
            >
              <option value="draft">Entwurf</option>
              <option value="published">Veröffentlicht</option>
            </select>

            <label className="block text-sm font-medium text-slate-400">Tags (kommagetrennt)</label>
            <input
              className="w-full border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded-lg px-3 py-2 outline-none focus:border-sky-400 placeholder:text-slate-600"
              value={tagsStr}
              onChange={(e) => setTagsStr(e.target.value)}
              placeholder="z. B. onboarding, trips, checklisten"
            />

            <label className="block text-sm font-medium text-slate-400">Inhalt (Markdown)</label>
            <textarea
              className="w-full border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded-lg px-3 py-2 h-64 outline-none focus:border-sky-400 placeholder:text-slate-600"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Dein Text in Markdown…"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-400">Vorschau</label>
            <div className="border border-[#2a3347] rounded-lg p-3 bg-[#1e2639] h-96 overflow-auto prose prose-invert max-w-none text-slate-300">
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
                {body || "*Hier erscheint deine Vorschau…*"}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6">
        <Link to="/admin/faq" className="text-sky-400 hover:underline text-sm">
          ← Zur Liste
        </Link>
      </div>
    </div>
  );
}
